import { Observable, of, Subject } from 'rxjs';
import { Component, OnInit, OnChanges, Input, Output, forwardRef, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SEMICOLON, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { switchMap, debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';

import { CustomerService } from './../../../api/customer.service';
import { UserProfileService } from '../../../api/user-profile.service';
import { TranslateService } from '../../../api/translate.service';
@Component({
  selector: 'iv-contact-field',
  templateUrl: './contact-field.component.html',
  styleUrls: ['./contact-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ContactFieldComponent),
      multi: true
    }
  ]
})
export class ContactFieldComponent implements OnInit, OnChanges, ControlValueAccessor {

  @Input() type: string;
  @Input() controlName: string;
  @Input() label: string;
  // tslint:disable-string-line: no-input-rename
  @Input('fieldValue') _value: string;
  @Input() customer;

  @Output() selectContact = new EventEmitter();
  @Output() focus = new EventEmitter();
  @Output() blur = new EventEmitter();

  @Input() disabled = false;
  @Input() required = false;

  toolTipText: string;
  separatorKeysCodes: number[] = [ENTER, SEMICOLON];

  @ViewChild('emailInput') emailInput: ElementRef<HTMLInputElement>;

  get fieldValue() {
    return this._value;
  }

  set fieldValue(val) {
    this._value = val;
    if (this.controlName !== 'bill_to_email_address' && this.controlName !== 'represented_contact_email') {
      this.propagateChange(val);
    }
  }

  field: string;
  emails = [];

  suggestions: Observable<any[]> = of([]);
  suggestionsInput$: Subject<string> = new Subject();

  propagateChange = (_: any) => { };
  propagateTouched = (_: any) => { };

  userCountry: string;

  constructor(private customerService: CustomerService,
    private userProfileService: UserProfileService,
    private translateService: TranslateService) {
    this.userCountry = this.userProfileService.getCountryLocalStorage();
  }

  ngOnInit() {
    this.field = 'name';
    if (this.type === 'email') {
      this.field = 'email';
      this.emails = this.splitEmails() ? this.splitEmails() : [];
    }

    this.suggestions = this.suggestionsInput$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap((userInput: string) => this.writeValue(userInput)),
      switchMap((userInput: string) => this.getSuggestions(userInput))
    );
  }

  ngOnChanges() {
    this.propagateChange(this.fieldValue);
    if (this.type === 'email') {
      this.field = 'email';
      this.emails = this.splitEmails() ? this.splitEmails() : [];
    }
    this.setSuggestions(this.fieldValue);
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.emails.push(value);
    }
    this.writeValue(this.emails.join(';'));
    this.propagateChange(this.emails.join(';'));
    event.chipInput!.clear();
    if (['CHN'].includes(this.userCountry) && this.emails?.length > 1) {
      this.remove(event.value);
    }
  }

  remove(email: string): void {
    const index = this.emails.indexOf(email);

    if (index >= 0) {
      this.emails.splice(index, 1);
    }
    this.writeValue(this.emails.join(';'));
    this.propagateChange(this.emails.join(';'));

  }

  splitEmails() {
    return this._value ? this._value.split(';') : null;
  }

  writeValue(value: string) {
    if (this.fieldValue !== value) {
      if (value === '') {
        value = null;
      }
      this.fieldValue = value;
    }
  }

  registerOnChange(fn) {
    this.propagateChange = fn;
  }

  registerOnTouched(fn) {
    this.propagateTouched = fn;
  }

  getSuggestions(val: string) {
    if (!val) {
      return of([]);
    }
    return this.customerService.searchContacts(this.field, val, this.customer);
  }

  setSuggestions(val: string) {
    this.suggestionsInput$.next(val);
  }

  displayValue() {
    return (value: any) => {
      if (value) {
        return value[this.field];
      }
      return this.fieldValue;
    };
  }

  contactSelectionChange(customer: any, event: any) {
    if (!event.source.selected) {
      return;
    }
    if (this.controlName !== 'bill_to_email_address' && this.controlName !== 'represented_contact_email') {
      this.selectContact.emit({ id: customer.id, name: customer.name });
    } else {
      this.emails.push(customer.email);
      this.selectContact.emit({ email: this.emails.join(';') });
      this.propagateChange(this.emails.join(';'));
      this.emailInput.nativeElement.value = '';
      if ((['CHN'].includes(this.userCountry) && this.emails?.length > 1)) {
        this.remove(customer.email);
      }
    }
    this.writeValue(customer[this.field]);
  }

  invokeBlur(event: string) {
    if (!event) {
      this.propagateChange(null);
      this.propagateTouched(null);
      this.blur.emit();
      this.writeValue(event);
    }
  }

  getMaxLengthEmail() {
    return (70 - this.emails.join(';').length < 0 ? 0 : 70 - this.emails.join(';').length);
  }

  getToolTipText() {
    return this.toolTipText = !['CHN'].includes(this.userCountry) ?
      this.translateService.getTranslation('MULTIEMAIL_TOOLTIP') :
      this.translateService.getTranslation('EMAIL_TOOLTIP');
  }
}